import { useApolloClient } from "@apollo/client";
import {
  applyScheduleRowPatch,
  mapFetchALL,
  useContentActions,
  useContentSchedule,
  usePage,
} from "@platformx/authoring-apis";
import {
  RootState,
  clearComment,
  previewContent,
  updateContentList,
} from "@platformx/authoring-state";
import {
  OLD_CONTENT_TYPES,
  ShowToastError,
  capitalizeFirstLetter,
  frameDocumentType,
} from "@platformx/utilities";
import { captureException } from "@sentry/react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import CreateNewPage from "../../pages/page/CreateNewPage";
import ContentListing from "../ContentListing/ContentListing";
import ActiveFilterChips from "../ContentListingHeader/ActiveFilterChips";
import ContentListingHeader from "../ContentListingHeader/ContentListingHeader";
import DateFilterModal, { FilterValues } from "../ContentListingHeader/DateFilterModal";
import { DEFAULT_CONTENT_SORT } from "../ContentListingHeader/helperContentList";
import CreateNewContent from "./CreateNewContent";
import { pickContentListQuery } from "./queryPicker";

const row = 100;
const ContListingContainer = ({
  contentType,
  schemaType,
}: {
  contentType: string;
  schemaType: string;
}) => {
  // const navigate = useNavigate();
  const dispatch = useDispatch();
  const { content, routes } = useSelector((state: RootState) => state);
  const normalizedContentType = contentType?.toLocaleLowerCase?.() || "";
  const currentRoute =
    routes?.routes.find(
      (x) =>
        x?.id?.toLocaleLowerCase?.() === normalizedContentType ||
        x?.subCategory?.toLocaleLowerCase?.() === normalizedContentType,
    ) || {};
  const { title, is_shared: isShared = false } = currentRoute;
  const { contentArray } = content;
  const client = useApolloClient();
  const location = useLocation();
  const { t } = useTranslation();
  const { state } = location;
  const [queryObject, setQueryObject] = useState({
    startIndex: 0,
    filter: "ALL",
  });
  const [isSpinning, setIsSpinning] = useState(false);
  const [isDialogOpen, setOpenCreatePage] = useState(false);
  const [loader, setloader] = useState(true);
  const updatedSchema = frameDocumentType(schemaType);

  // Date filter — the URL is the single source of truth
  // (?from=YYYY-MM-DD&to=YYYY-MM-DD), so a filtered listing is shareable and
  // survives a refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDateFilterOpen, setDateFilterOpen] = useState(false);
  const statusFilter: string[] = (searchParams.get("status") || "").split(",").filter(Boolean);
  const authorFilter: string[] = (searchParams.get("author") || "").split(",").filter(Boolean);
  const filterValues: FilterValues = {
    fromDate: searchParams.get("from") || "",
    toDate: searchParams.get("to") || "",
    status: statusFilter,
    author: authorFilter,
  };
  const searchTerm = searchParams.get("q") || "";
  // Sort choice — the URL is the single source of truth (?sort=<key>), so a
  // sorted listing is shareable and survives a refresh, matching the date
  // filter and search. Defaults to "newest" (lastModificationDate DESC), which
  // is the pre-existing ordering.
  const sortValue = searchParams.get("sort") || DEFAULT_CONTENT_SORT;
  const activeDateFilterCount = filterValues.fromDate || filterValues.toDate ? 1 : 0;
  const activeStatusFilterCount = statusFilter.length > 0 ? 1 : 0;
  const activeAuthorFilterCount = authorFilter.length > 0 ? 1 : 0;
  const activeFilterCount =
    activeDateFilterCount + activeStatusFilterCount + activeAuthorFilterCount;
  // Course uses a different query (FETCH_COURSE_LIST) that cannot take a
  // dateFilter, searchTerm or sortOptions, so those controls are suppressed.
  const isCourse = contentType?.toLocaleLowerCase() === "course";
  const prevContentType = useRef(contentType);

  const mergedFetchState = {
    ...(state || {}),
    fromDate: filterValues.fromDate || state?.fromDate || undefined,
    toDate: filterValues.toDate || state?.toDate || undefined,
    searchTerm: searchTerm || state?.searchTerm || undefined,
    sortBy: sortValue,
    author: authorFilter,
  };

  // Falls back to the legacy single-select for Course, which doesn't use statusFilter.
  const pageFilter: string | string[] = statusFilter.length ? statusFilter : queryObject.filter;

  const contentVariables: any = mapFetchALL(
    mergedFetchState,
    pageFilter,
    contentType,
    {
      start: queryObject.startIndex,
      rows: row,
    },
    updatedSchema,
  );

  // The query and variables for the listing, shared by the page loads and the post-schedule refresh so
  // the two can never disagree about filters, search, sort or content-type spelling.
  const buildListRequest = (baseVariables = contentVariables) => {
    const variableCourse: any = { ...baseVariables, filter: "Course", isListing: true };
    // Only the title sorts populate `sortOptions`; that case uses the query
    // variant that declares $sortOptions. Every other case keeps the base query, so it never
    //  breaks on an environment whose gateway has not deployed that backend yet.
    const fetchQuery = pickContentListQuery(
      contentType?.toLocaleLowerCase() === "course",
      Boolean(baseVariables?.sortOptions),
    );

    const variables =
      contentType?.toLocaleLowerCase() === "course" ? variableCourse : baseVariables;
    const requestVariables = OLD_CONTENT_TYPES.some(
      (item) => item.toLocaleLowerCase() === variables?.contentType?.toLocaleLowerCase(),
    )
      ? variables?.contentType.toLowerCase() === "formbuilder"
        ? { ...variables, contentType: contentType }
        : { ...variables, contentType: capitalizeFirstLetter(contentType) }
      : { ...variables };
    return { fetchQuery, requestVariables };
  };

  //common listing for content types
  const fetchListingData = async () => {
    try {
      const { fetchQuery, requestVariables } = buildListRequest();

      const response = await client.query({
        query: fetchQuery,
        variables: requestVariables,
        context: {
          skipBrandNameHeader: isShared,
        },
      });
      if (queryObject.startIndex > 0) {
        const newContent = [
          ...contentArray,
          ...(response?.data?.authoring_getContentTypeItems ||
            response?.data?.authoring_recentContents ||
            // response?.data?.authoring_getContentListing ||
            []),
        ];
        dispatch(previewContent({}));
        dispatch(clearComment());
        dispatch(updateContentList(newContent));
      } else if (queryObject.startIndex === 0) {
        const newContent = [
          ...(response?.data?.authoring_getContentTypeItems ||
            response?.data?.authoring_recentContents ||
            // response?.data?.authoring_getContentListing ||
            []),
        ];
        setIsSpinning(false);
        dispatch(previewContent({}));
        dispatch(clearComment());
        dispatch(updateContentList(newContent));
      }
    } catch (err: any) {
      console.error(err);
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
      captureException(err);
    } finally {
      setloader(false);
    }
  };

  // After a reschedule or cancel from the row menu: re-read the listing the way the screen itself reads
  // it (same filters, search and sort), keeping as many rows as are already loaded so a scrolled list
  // does not snap back to page one. The changed row gets the value we know is true, because the store
  // behind the listing can lag the mutation and would otherwise hand the old time straight back.
  const refreshListAfterSchedule = async (
    item: any,
    patch?: Partial<Record<string, string | null>>,
  ) => {
    try {
      const { fetchQuery, requestVariables } = buildListRequest({
        ...contentVariables,
        pagination: { start: 0, rows: Math.max(row, contentArray.length) },
      });
      const response = await client.query({
        query: fetchQuery,
        variables: requestVariables,
        fetchPolicy: "network-only",
        context: { skipBrandNameHeader: isShared },
      });
      const rows = response?.data?.authoring_getContentTypeItems || [];
      dispatch(updateContentList(applyScheduleRowPatch(rows, item, patch)));
    } catch (err) {
      // The change succeeded; at least show it on the row that is already on screen.
      dispatch(updateContentList(applyScheduleRowPatch(contentArray, item, patch)));
      throw err;
    }
  };

  //Triggered when the filter is changed and on load
  useEffect(() => {
    fetchListingData();
  }, [queryObject]);

  //Triggered when the common search is changed and when left menu is navigated.
  useEffect(() => {
    if (state || contentType) {
      setloader(true);
      //filter and startindex is reset to default when the content type is changed.
      setQueryObject({ ...queryObject, startIndex: 0, filter: "ALL" });
    }
  }, [state, contentType]);

  //Clear the date filter, search and sort when navigating between content
  //types, but NOT on the first mount, so a pasted/refreshed filtered URL is
  //still honoured.
  useEffect(() => {
    if (prevContentType.current !== contentType) {
      prevContentType.current = contentType;
      if (
        searchParams.get("from") ||
        searchParams.get("to") ||
        searchParams.get("q") ||
        searchParams.get("sort") ||
        searchParams.get("status") ||
        searchParams.get("author")
      ) {
        const params = new URLSearchParams(searchParams);
        params.delete("from");
        params.delete("to");
        params.delete("q");
        params.delete("sort");
        params.delete("status");
        params.delete("author");
        setSearchParams(params, { replace: true });
      }
    }
  }, [contentType]);

  const closeButtonHandle = () => {
    setOpenCreatePage(false);
  };

  const {
    deleteContent,
    duplicate,
    preview,
    unPublish,
    view,
    edit,
    editDynamicContent,
    editPage,
    fetchContentDetails,
    duplicateToSite,
    shareContentWithBrands,
    fetchSchemaContentDetails,
    loading: deleteLoading,
  } = useContentActions("ALL", isShared);
  const { handlePageDelete, loading: deletePageLoading } = usePage();
  // KIWI-6576: amend / cancel a pending schedule from the row menu.
  const { reschedule: rescheduleSchedule, cancel: cancelScheduleOnItem } = useContentSchedule(
    "ALL",
    isShared,
    refreshListAfterSchedule,
  );

  const memoizedMethods = useMemo(
    () => ({
      deleteContent,
      duplicate,
      preview,
      unPublish,
      view,
      edit,
      editPage,
      fetchContentDetails,
      duplicateToSite,
      shareContentWithBrands,
      fetchSchemaContentDetails,
      handlePageDelete,
      editDynamicContent,
    }),
    [
      deleteContent,
      duplicate,
      preview,
      unPublish,
      view,
      edit,
      editPage,
      fetchContentDetails,
      duplicateToSite,
      shareContentWithBrands,
      fetchSchemaContentDetails,
      handlePageDelete,
      editDynamicContent,
    ],
  );

  const createContentNew = () => {
    // if (
    //   contentType?.trim()?.toLowerCase() === "sitepage" ||
    //   contentType?.trim()?.toLowerCase() === "formbuilder"
    // ) {
    setOpenCreatePage(true);
    // }
    //  else {
    //   const navigateTo = makeCreateContentPath(contentType);
    //   navigate(navigateTo, {
    //     state: contentType?.trim()?.toLowerCase(),
    //   });
    // }
  };

  const handleFilter = (filter: string) => {
    const newQueryObject = { ...queryObject, filter: filter, startIndex: 0 };
    setQueryObject(newQueryObject);
    setloader(true);
  };

  const handleRefresh = () => {
    setloader(true);
    setIsSpinning(true);
    const newQueryObject = { ...queryObject, startIndex: 0 };
    setQueryObject(newQueryObject);
  };

  const fetchMoreData = () => {
    const newQueryObject = { ...queryObject, startIndex: queryObject.startIndex + row };
    setQueryObject(newQueryObject);
  };

  const applyFilters = (next: FilterValues) => {
    setloader(true);
    const params = new URLSearchParams(searchParams);
    if (next.fromDate) params.set("from", next.fromDate);
    else params.delete("from");
    if (next.toDate) params.set("to", next.toDate);
    else params.delete("to");
    if (next.status?.length) params.set("status", next.status.join(","));
    else params.delete("status");
    if (next.author?.length) params.set("author", next.author.join(","));
    else params.delete("author");
    // replace (not push) so Back doesn't wipe the global-search state that the
    // dashboard header owns via window.onpopstate.
    setSearchParams(params, { replace: true });
    // new object ref → refetch from the top with the merged date/status/author variables.
    setQueryObject((prev) => ({ ...prev, startIndex: 0 }));
  };

  const applySearch = (term: string) => {
    if (term === searchTerm) return;
    setloader(true);
    const params = new URLSearchParams(searchParams);
    if (term) params.set("q", term);
    else params.delete("q");
    setSearchParams(params, { replace: true });
    setQueryObject((prev) => ({ ...prev, startIndex: 0 }));
  };

  const applySort = (next: string) => {
    if (next === sortValue) return;
    setloader(true);
    const params = new URLSearchParams(searchParams);
    // Keep the URL clean: the default sort carries no param.
    if (next && next !== DEFAULT_CONTENT_SORT) params.set("sort", next);
    else params.delete("sort");
    setSearchParams(params, { replace: true });
    // new object ref → refetch from the top with the new sort variables.
    setQueryObject((prev) => ({ ...prev, startIndex: 0 }));
  };

  const removeStatusFilter = (value: string) => {
    applyFilters({ ...filterValues, status: statusFilter.filter((v) => v !== value) });
  };

  const removeAuthorFilter = (value: string) => {
    applyFilters({ ...filterValues, author: authorFilter.filter((v) => v !== value) });
  };

  const clearAllFilters = () => {
    applyFilters({ fromDate: "", toDate: "", status: [], author: [] });
  };

  const unpublishContentType = (contentTypeItem) => {
    memoizedMethods.unPublish(contentTypeItem, updatedSchema);
  };

  const rescheduleContentType = (contentTypeItem, kind, isoTime) =>
    rescheduleSchedule(contentTypeItem, updatedSchema, kind, isoTime);

  const cancelContentSchedule = (contentTypeItem, kind) =>
    cancelScheduleOnItem(contentTypeItem, updatedSchema, kind);

  const deleteContentType = (contentTypeItem) => {
    memoizedMethods.deleteContent(
      contentTypeItem,
      {
        ...queryObject,
        startIndex: 0,
      },
      updatedSchema,
    );
  };
  const deletePageList = (pageItem) => {
    handlePageDelete(pageItem, {
      ...queryObject,
      startIndex: 0,
    });
  };

  // Same query the editor uses when opening a schema-based document in edit mode.
  const fetchShareBrandDetails = (contentTypeItem) =>
    memoizedMethods.fetchSchemaContentDetails({
      contentType: contentTypeItem?.tag_name,
      documentId: contentTypeItem?.page,
      schemaType: updatedSchema,
    });

  const shareContentTypeWithBrands = async (
    contentTypeItem,
    primaryBrandId: string,
    selectedBrandIds: string[],
    prefetchedDoc?: any,
  ) => {
    const result = await memoizedMethods.shareContentWithBrands(
      contentTypeItem,
      primaryBrandId,
      selectedBrandIds,
      updatedSchema,
      prefetchedDoc,
    );
    // Refetch so the next Share With Brands preselects fresh values.
    if (result) handleRefresh();
    return result;
  };

  return (
    <>
      <ContentListingHeader
        handleFilter={handleFilter}
        title={
          contentType?.toLocaleLowerCase() === "all"
            ? "Result"
            : contentType?.toLocaleLowerCase() === "sitepage"
              ? "Page"
              : title || contentType
        }
        category={contentType === "sitepage" ? "Page" : schemaType}
        subCategory={contentType}
        handleAddNew={createContentNew}
        handleRefresh={handleRefresh}
        animationState={isSpinning}
        filterValue={queryObject.filter}
        onOpenDateFilter={isCourse ? undefined : () => setDateFilterOpen(true)}
        activeFilterCount={activeFilterCount}
        isDateFilterOpen={isDateFilterOpen}
        onSearchTerm={isCourse ? undefined : applySearch}
        searchValue={searchTerm}
        onSortChange={isCourse ? undefined : applySort}
        sortValue={sortValue}
      />

      <DateFilterModal
        open={isDateFilterOpen}
        onClose={() => setDateFilterOpen(false)}
        value={filterValues}
        onApply={applyFilters}
      />

      <ActiveFilterChips
        status={statusFilter}
        author={authorFilter}
        onRemoveStatus={removeStatusFilter}
        onRemoveAuthor={removeAuthorFilter}
        onClearAll={clearAllFilters}
      />

      <ContentListing
        content={contentType}
        loader={loader}
        deleteContent={deleteContentType}
        deletePage={deletePageList}
        fetchMoreData={fetchMoreData}
        preview={memoizedMethods.preview}
        unPublish={unpublishContentType}
        reschedule={rescheduleContentType}
        cancelSchedule={cancelContentSchedule}
        view={memoizedMethods.view}
        edit={memoizedMethods.edit}
        editDynamicContent={memoizedMethods.editDynamicContent}
        editPage={memoizedMethods.editPage}
        deleteContentLoading={deleteLoading}
        deletePageLoading={deletePageLoading}
        duplicate={memoizedMethods.duplicate}
        fetchContentDetails={memoizedMethods.fetchContentDetails}
        duplicateToSite={memoizedMethods.duplicateToSite}
        shareContentWithBrands={shareContentTypeWithBrands}
        fetchShareBrandDetails={fetchShareBrandDetails}
        schemaType={updatedSchema}
        row={row}
      />

      {contentType?.trim()?.toLowerCase() === "sitepage" ? (
        <CreateNewPage isDialogOpen={isDialogOpen} closeButtonHandle={closeButtonHandle} />
      ) : (
        <CreateNewContent
          isDialogOpen={isDialogOpen}
          closeButtonHandle={closeButtonHandle}
          contentType={contentType}
          category={schemaType}
          schemaDocumentType={updatedSchema}
          isShared={isShared}
        />
      )}
    </>
  );
};

export default memo(ContListingContainer);
